import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import PageContainer from '../../../shared/layout/containers/PageContainer';
import PageHeader from '../../../shared/layout/headers/PageHeader';
import PageTitle from '../../../shared/layout/titles/PageTitle';
import PageContent from '../../../shared/layout/containers/PageContent';
import MainButton from '../../../shared/layout/buttons/MainButton';
import AdminFilterBar from './components/AdminFilterBar';
import AdminDataTable from './components/AdminDataTable';
import FreshnessBadge from './components/FreshnessBadge';
import { fetchAdminTransactions, fetchAdminTransactionStats } from '../../../services/Http/AdminApi';
import StatCard from '../../../shared/layout/cards/StatCard';
import StatsGrid from '../../../shared/layout/containers/StatsGrid';
import { useCurrency } from '../../../context/CurrencyProvider';
import { SimpleSelect } from '../../../shared/layout/forms/AppSelect';

const AdminTransactions = () => {
    const { formatEurAmount, formatResolvedAmount } = useCurrency();
    const [rows, setRows] = useState([]);
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [period, setPeriod] = useState('30d');

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [listResp, statsResp] = await Promise.all([
                fetchAdminTransactions({ period, search, per_page: 25 }),
                fetchAdminTransactionStats({ period, search }),
            ]);
            setRows(listResp?.data || []);
            setStats(statsResp?.data || null);
        } catch (error) {
            toast.error(error?.response?.data?.message || 'Failed to load transactions');
        } finally {
            setLoading(false);
        }
    }, [period, search]);

    useEffect(() => {
        load();
    }, [load]);

    const columns = useMemo(() => ([
        { key: 'id', label: 'ID' },
        { key: 'date_of_transaction', label: 'Date', render: (row) => row.date_of_transaction ? new Date(row.date_of_transaction).toLocaleString() : '—' },
        { key: 'transaction_type', label: 'Type' },
        { key: 'payment_method', label: 'Payment' },
        { key: 'status', label: 'Status' },
        { key: 'quantity', label: 'Qty' },
        {
            key: 'price',
            label: 'Sale Price',
            render: (row) => formatResolvedAmount({
                eurAmount: row.price,
                localAmount: row.price_buyer_local,
                localCurrencyId: row.buyer_currency_id,
            }),
        },
        {
            key: 'cost_price',
            label: 'Cost Price',
            render: (row) => row.cost_price == null ? '—' : formatResolvedAmount({
                eurAmount: row.cost_price,
                localAmount: row.cost_price_seller_local,
                localCurrencyId: row.seller_currency_id,
            }),
        },
        {
            key: 'gross_margin',
            label: 'Gross Margin',
            render: (row) => row.gross_margin == null ? '—' : formatResolvedAmount({
                eurAmount: row.gross_margin,
                localAmount: row.gross_margin_seller_local,
                localCurrencyId: row.seller_currency_id,
            }),
        },
        {
            key: 'transaction_fee',
            label: 'Fee',
            render: (row) => row.transaction_fee == null ? '—' : formatResolvedAmount({
                eurAmount: row.transaction_fee,
                localAmount: row.transaction_fee_seller_local,
                localCurrencyId: row.seller_currency_id,
            }),
        },
        {
            key: 'revenue',
            label: 'Net Revenue',
            render: (row) => formatResolvedAmount({
                eurAmount: row.revenue,
                localAmount: row.revenue_seller_local,
                localCurrencyId: row.seller_currency_id,
            }),
        },
    ]), [formatEurAmount, formatResolvedAmount]);

    return (
        <PageContainer>
            <PageHeader>
                <PageTitle>Admin Transactions</PageTitle>
            </PageHeader>
            <PageContent>
                <AdminFilterBar>
                    <FilterRow>
                        <SearchInput value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search transactions" />
                        <PeriodSelect value={period} onChange={(e) => setPeriod(e.target.value)}>
                            <option value="7d">Last 7 days</option>
                            <option value="30d">Last 30 days</option>
                            <option value="90d">Last 90 days</option>
                        </PeriodSelect>
                    </FilterRow>
                    <FilterRow>
                        <FreshnessBadge status="success" sourceType="operational" />
                        <MainButton size="small" onClick={load}>Refresh</MainButton>
                    </FilterRow>
                </AdminFilterBar>

                {stats && (
                    <StatsGrid>
                        <StatCard icon="T" title="Transactions" value={formatNumber(stats.transactions_count)} color="transactions" />
                        <StatCard icon="G" title="GMV" value={formatEurAmount(stats.gmv)} color="totalVolume" />
                        <StatCard icon="R" title="Revenue" value={formatEurAmount(stats.revenue)} color="revenueValue" />
                        <StatCard icon="F" title="Fee Amount" value={formatEurAmount(stats.transaction_fee_amount)} color="info" />
                    </StatsGrid>
                )}

                <TableSpacer />
                <AdminDataTable columns={columns} rows={rows} loading={loading} emptyText="No transactions found" />
            </PageContent>
        </PageContainer>
    );
};

export default AdminTransactions;

const formatNumber = (value) => Number(value || 0).toLocaleString();

const FilterRow = styled.div`
    display: flex;
    align-items: center;
    gap: 0.625rem;
    flex-wrap: wrap;
`;

const SearchInput = styled.input`
    width: 280px;
    border: 1px solid ${({ theme }) => theme.colors.border || '#e5e7eb'};
    border-radius: 10px;
    padding: 0.5625rem 0.75rem;
    background: ${({ theme }) => theme.colors.surface || '#fff'};
`;

const PeriodSelect = styled(SimpleSelect)`
    border: 1px solid ${({ theme }) => theme.colors.border || '#e5e7eb'};
    border-radius: 10px;
    padding: 0.5625rem 0.75rem;
    background: ${({ theme }) => theme.colors.surface || '#fff'};
`;

const TableSpacer = styled.div`
    height: 16px;
`;
